import {
	map,
	prop,
	addIndex,
	length,
	always,
	defaultTo,
	F,
	compose,
	not,
	isEmpty,
	all,
	equals,
	path
} from 'ramda';
import Loader from './Loader';
import Pagination from './Pagination';

const mapIndexed = addIndex(map);
const emptyArray = always([])
const emptyObj = always({})
const noop = always(undefined)
const notEmpty = compose(not, isEmpty)
const allTrue = all(equals(true))

/** @type { import('vue').FunctionalComponentOptions } */
const SimpleTable = ({ slots, data }) => (
	<table { ...data }>
		{ slots().default }
	</table>
)
SimpleTable.name = 'SimpleTable';


/** @type { import('vue').FunctionalComponentOptions } */
const SimpleRow = ({ slots, data }) => (
	<tr { ...data }>
		{ slots().default }
	</tr>
)
SimpleRow.name = 'SimpleRow';

/**
 * @typedef ColumnPropsType
 * @property { T } record
 * @property { any } value
 * @property { number } index
 * @template T
 */

/**
 * @typedef Column
 * @property { string } [dataIndex]
 * @property { string } [name]
 * @property { import('vue/types/options').Component<Data, Methods, Computed, ColumnPropsType<Type>> } [render]
 * @template Type, Data, Methods, Computed
 */

/**
 * @typedef Components
 * @property { import('vue/types/options').Component } row
 */

/**
 * @typedef TableProps
 * @property { boolean } [bordered]
 * @property { boolean } [borderless]
 * @property { boolean } [striped]
 * @property { boolean } showHeader
 * @property { boolean } loading
 * @property { Column[] } columns
 * @property { array } dataSource
 * @property { () => string } rowClassName
 * @property { boolean | import('./Pagination').PaginationProps } [pagination]
 * @property { { body: Components, header: Components, table: import('vue/types/options').Component } } components
 */

/** @type { import('vue/types/options').FunctionalComponentOptions<TableProps> } */
const Table = {
	functional: true,
	name: 'Table',
	props: {
		bordered: Boolean,
		borderless: Boolean,
		striped: Boolean,
		showHeader: {
			type: Boolean,
			default: true,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		columns: {
			type: Array,
			default: emptyArray,
		},
		dataSource: {
			type: Array,
			default: emptyArray,
		},
		rowClassName: {
			type: Function,
			default: noop,
		},
		pagination: {
			type: [Boolean, Object],
			default: F,
		},
		components: {
			type: Object,
			default: emptyObj,
		},
	},
	render(h, { props, data }) {
		const {
			bordered,
			borderless,
			striped,
			columns,
			dataSource: records,
			showHeader,
			loading,
			rowClassName,
			components,
			pagination,
		} = props;

		const TableComponent = defaultTo(SimpleTable, components.table);
		const HeaderRowComponent = defaultTo(SimpleRow, components.header?.row);
		const BodyRowComponent = defaultTo(SimpleRow, components.body?.row);

		return (
			<Loader loading={ loading } { ...data }>
				<TableComponent class={{
					'table': true,
					'table-striped': striped,
					'table-borderless': borderless,
					'table-bordered': bordered,
				}}>
					{ showHeader ? (
						<thead>
							<HeaderRowComponent>
								{ map(({ name }) => (
									<td>
										{ name }
									</td>
								), columns) }
							</HeaderRowComponent>
						</thead>
					) : null }
					<tbody>
						{ length(records) ? mapIndexed((record, idx) => (
							<BodyRowComponent class={ rowClassName(record, idx) }>
								{ map(({ dataIndex, render: Component }) => {
									const value = Array.isArray(dataIndex) ? path(dataIndex, record) : prop(dataIndex, record);

									return (
										<td>
											{ Component ? (
												<Component
													value={ value }
													record={ record }
													idx={ idx }
												/>
											) : value }
										</td>
									)
								}, columns) }
							</BodyRowComponent>
						), records) : (
							<BodyRowComponent>
								<td colSpan={ length(columns) }>
									Нет данных
								</td>
							</BodyRowComponent>
						)}
					</tbody>
				</TableComponent>
				{ allTrue([!!pagination, notEmpty(records)]) ? (
					<Pagination
						total={ defaultTo(records.length, pagination.total) }
						pageSize={ pagination.pageSize }
						current={ pagination.current }
						sortArray={ pagination.sortArray }
						onChange={ pagination.onChange }
					/>
				) : null }
			</Loader>
		);
	}
}

export const ColumnProps = {
	value: [String, Number, Object],
	record: Object,
	idx: Number,
}

export default Table;
